import type { ApplySuggestionHandler } from "@/components/Copilot/applySuggestion/applySuggestionHandler"
import type { Agent } from "@/types"
import type { KnowledgeBaseFolder } from "@/hooks/api"
import type { UnifiedTool as FormUnifiedTool } from "@/pages/Workspace/components/Agent/type"
import type { HttpRequestToolConfig } from "@/types/httpRequestTool"
import { isHttpInputContract } from "@/lib/httpRequestInputs"
import type { RequestFormSchema } from "@/types/requestForm"
import type { Text2SpeechConfig } from "@/types/text2speechTool"

function generateId(toolType: string): string {
  return `${toolType}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

function generateRequestFormId(): string {
  return Math.random().toString(36).slice(2)
}

const HTTP_METHODS = new Set(["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD"])
const HTTP_AUTH_TYPES = new Set([
  "none",
  "bearer",
  "apiKey",
  "basic",
  "customHeader",
])
const REQUEST_FORM_FIELD_TYPES = new Set([
  "shortText",
  "longText",
  "number",
  "dropdown",
  "radio",
  "checkbox",
  "date",
  "file",
])
const REQUEST_FORM_LABEL_REGEX = /^[a-z0-9_-]+$/

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.map((v) => String(v)).filter(Boolean)
}

function normalizeDescriptionImage(
  value: unknown,
  knownImageUrls: ReadonlyMap<string, string>
): RequestFormSchema["fields"][number]["descriptionImage"] | null {
  if (!value || typeof value !== "object") return null
  const image = value as Record<string, unknown>
  if (
    typeof image.attachmentId !== "string" ||
    !image.attachmentId.trim() ||
    typeof image.altText !== "string" ||
    !image.altText.trim()
  ) {
    return null
  }
  // Suggestions often omit or invent the URL. Keep the editor URL of an image
  // already configured; the backend rejects unknown images and rewrites URLs.
  return {
    attachmentId: image.attachmentId,
    altText: image.altText,
    url:
      knownImageUrls.get(image.attachmentId) ??
      (typeof image.url === "string" ? image.url : ""),
  }
}

function normalizeRequestFormSchema(
  value: unknown,
  knownImageUrls: ReadonlyMap<string, string>
): RequestFormSchema | null {
  if (!value || typeof value !== "object") return null
  const schema = value as Record<string, unknown>
  const title = typeof schema.title === "string" ? schema.title.trim() : ""
  if (!title || !Array.isArray(schema.fields)) return null

  const fields: RequestFormSchema["fields"] = []
  for (const field of schema.fields) {
    if (!field || typeof field !== "object") return null
    const f = field as Record<string, unknown>
    const type = typeof f.type === "string" ? f.type : ""
    const question = typeof f.question === "string" ? f.question : ""
    const label = typeof f.label === "string" ? f.label : ""
    if (
      !REQUEST_FORM_FIELD_TYPES.has(type) ||
      !question.trim() ||
      !label.trim() ||
      !REQUEST_FORM_LABEL_REGEX.test(label)
    ) {
      return null
    }
    const required = typeof f.required === "boolean" ? f.required : false
    const placeholder = typeof f.placeholder === "string" ? f.placeholder : ""
    let descriptionImage: RequestFormSchema["fields"][number]["descriptionImage"]
    if (f.descriptionImage != null) {
      const normalizedImage = normalizeDescriptionImage(
        f.descriptionImage,
        knownImageUrls
      )
      if (!normalizedImage) return null
      descriptionImage = normalizedImage
    }

    const optionsRaw = f.options
    let options: RequestFormSchema["fields"][number]["options"] | undefined
    if (optionsRaw != null) {
      if (!Array.isArray(optionsRaw)) return null
      options = []
      for (const option of optionsRaw) {
        if (!option || typeof option !== "object") return null
        const opt = option as Record<string, unknown>
        if (typeof opt.label !== "string" || typeof opt.value !== "string") {
          return null
        }
        options.push({ label: opt.label, value: opt.value })
      }
    }

    const fieldId =
      typeof f.id === "string" && f.id.trim() ? f.id : generateRequestFormId()
    const rawName = typeof f.name === "string" ? f.name.trim() : ""
    const fieldName =
      rawName ||
      `${type as RequestFormSchema["fields"][number]["type"]}_${generateRequestFormId().slice(0, 4)}`

    fields.push({
      id: fieldId,
      type: type as RequestFormSchema["fields"][number]["type"],
      name: fieldName,
      question,
      label,
      required,
      placeholder,
      helpText: typeof f.helpText === "string" ? f.helpText : undefined,
      descriptionImage,
      defaultValue:
        Object.prototype.hasOwnProperty.call(f, "defaultValue") &&
        f.defaultValue !== undefined
          ? f.defaultValue
          : undefined,
      min: typeof f.min === "number" ? f.min : undefined,
      max: typeof f.max === "number" ? f.max : undefined,
      step: typeof f.step === "number" ? f.step : undefined,
      options,
    })
  }

  return {
    id:
      typeof schema.id === "string" && schema.id.trim()
        ? schema.id
        : generateRequestFormId(),
    title,
    description:
      typeof schema.description === "string" ? schema.description : undefined,
    fields,
  }
}

function normalizeHttpRequest(
  value: unknown
): HttpRequestToolConfig | undefined {
  if (!value || typeof value !== "object") return undefined
  const req = value as Record<string, unknown>
  const method = String(req.method ?? "")
  const urlTemplate = String(req.urlTemplate ?? "")
  const authRaw = req.auth
  if (!HTTP_METHODS.has(method) || !urlTemplate) return undefined
  if (!authRaw || typeof authRaw !== "object") return undefined

  const auth = authRaw as Record<string, unknown>
  const authType = String(auth.type ?? "")
  if (!HTTP_AUTH_TYPES.has(authType)) return undefined

  const headers = Array.isArray(req.headers)
    ? req.headers
        .filter((item) => item && typeof item === "object")
        .map((item) => ({
          key: String((item as Record<string, unknown>).key ?? ""),
          value: String((item as Record<string, unknown>).value ?? ""),
        }))
    : []
  const queryParams = Array.isArray(req.queryParams)
    ? req.queryParams
        .filter((item) => item && typeof item === "object")
        .map((item) => ({
          key: String((item as Record<string, unknown>).key ?? ""),
          value: String((item as Record<string, unknown>).value ?? ""),
        }))
    : []

  const bodyRaw =
    req.body && typeof req.body === "object"
      ? (req.body as Record<string, unknown>)
      : {}
  const inputContracts = bodyRaw.inputContracts
  if (
    inputContracts !== undefined &&
    (inputContracts === null ||
      typeof inputContracts !== "object" ||
      Array.isArray(inputContracts) ||
      !Object.values(inputContracts).every(isHttpInputContract))
  )
    return undefined

  return {
    method: method as HttpRequestToolConfig["method"],
    urlTemplate,
    auth: {
      type: authType as HttpRequestToolConfig["auth"]["type"],
      token:
        typeof auth.token === "string" ? (auth.token as string) : undefined,
      apiKey:
        typeof auth.apiKey === "string" ? (auth.apiKey as string) : undefined,
      apiKeyLocation:
        auth.apiKeyLocation === "header" || auth.apiKeyLocation === "query"
          ? (auth.apiKeyLocation as "header" | "query")
          : undefined,
      apiKeyParamName:
        typeof auth.apiKeyParamName === "string"
          ? (auth.apiKeyParamName as string)
          : undefined,
      username:
        typeof auth.username === "string"
          ? (auth.username as string)
          : undefined,
      password:
        typeof auth.password === "string"
          ? (auth.password as string)
          : undefined,
      headerName:
        typeof auth.headerName === "string"
          ? (auth.headerName as string)
          : undefined,
      headerValue:
        typeof auth.headerValue === "string"
          ? (auth.headerValue as string)
          : undefined,
    },
    headers,
    queryParams,
    body: {
      template: bodyRaw.template,
      ...(inputContracts === undefined
        ? {}
        : {
            inputContracts:
              inputContracts as HttpRequestToolConfig["body"]["inputContracts"],
          }),
    },
  }
}

function normalizeText2Speech(value: unknown): Text2SpeechConfig | undefined {
  if (!value || typeof value !== "object") return undefined
  const config = value as Record<string, unknown>
  const provider =
    typeof config.provider === "string" ? config.provider.trim() : ""
  const voiceId =
    typeof config.voiceId === "string" ? config.voiceId.trim() : ""
  const modelId =
    typeof config.modelId === "string" ? config.modelId.trim() : ""
  const outputFormat =
    typeof config.outputFormat === "string" ? config.outputFormat.trim() : ""
  const languageCode =
    typeof config.languageCode === "string" && config.languageCode.trim()
      ? config.languageCode.trim()
      : undefined

  if (provider !== "elevenlabs" || !voiceId || !modelId || !outputFormat) {
    return undefined
  }

  return {
    provider: "elevenlabs",
    voiceId,
    modelId,
    languageCode,
    outputFormat,
  }
}

function normalizeSchemaItem(
  item: Record<string, unknown>,
  opts: {
    validAgentIds: Set<string>
    tabularToolsEnabled: boolean
    knownImageUrls: ReadonlyMap<string, string>
  }
): { tool?: FormUnifiedTool; hardFail?: boolean } {
  const type = String(item.type ?? "")
  if (!type) return {}

  const toolBase: FormUnifiedTool = {
    id:
      typeof item.id === "string" && item.id.trim()
        ? item.id
        : generateId(String(type) || "tool"),
    type: type as FormUnifiedTool["type"],
    name: typeof item.name === "string" ? item.name : undefined,
    description:
      typeof item.description === "string" ? item.description : undefined,
    version: typeof item.version === "number" ? item.version : undefined,
    domain: typeof item.domain === "string" ? item.domain : undefined,
    invocationInstruction:
      typeof item.invocationInstruction === "string"
        ? item.invocationInstruction
        : undefined,
    agentId: typeof item.agentId === "string" ? item.agentId : undefined,
    knowledgeNodeIds: toStringArray(item.knowledgeNodeIds),
    knowledgeNodeId:
      typeof item.knowledgeNodeId === "string" && item.knowledgeNodeId.trim()
        ? item.knowledgeNodeId.trim()
        : undefined,
    imageModel:
      typeof item.imageModel === "string" && item.imageModel.trim()
        ? item.imageModel
        : undefined,
    customFormSchema:
      item.customFormSchema && typeof item.customFormSchema === "object"
        ? (item.customFormSchema as FormUnifiedTool["customFormSchema"])
        : undefined,
    httpRequest: normalizeHttpRequest(item.httpRequest),
    text2speech: normalizeText2Speech(item.text2speech),
  }

  if (type === "knowledge_base") {
    if ((toolBase.knowledgeNodeIds ?? []).length === 0) return {}
    return { tool: toolBase }
  }

  if (type === "tabular_data") {
    if (!opts.tabularToolsEnabled || !toolBase.knowledgeNodeId) {
      return { hardFail: true }
    }
    return { tool: toolBase }
  }

  if (type === "web_search") {
    return { tool: toolBase }
  }

  if (type === "call_agent") {
    const aid = toolBase.agentId ? String(toolBase.agentId) : ""
    if (!aid || !opts.validAgentIds.has(aid)) return {}
    return { tool: toolBase }
  }

  if (type === "request_form") {
    const normalizedSchema = normalizeRequestFormSchema(
      toolBase.customFormSchema,
      opts.knownImageUrls
    )
    if (!normalizedSchema) {
      return { hardFail: true }
    }
    toolBase.customFormSchema = normalizedSchema
    return { tool: toolBase }
  }

  if (type === "memory") {
    return { tool: toolBase }
  }

  if (type === "image_generation") {
    if (!toolBase.imageModel) return {}
    return { tool: toolBase }
  }

  if (type === "payment") {
    return {
      tool: {
        ...toolBase,
        name: toolBase.name || "Payment",
      },
    }
  }

  if (type === "http_request") {
    if (!toolBase.httpRequest) return {}
    return { tool: toolBase }
  }

  if (type === "text2speech") {
    if (!toolBase.text2speech) return {}
    return { tool: toolBase }
  }

  // Unknown tool type => skip
  return {}
}

export function makeUnifiedToolsApplyHandler(opts: {
  knowledgeBases: KnowledgeBaseFolder[]
  agents: Agent[]
  tabularToolsEnabled?: boolean
  /** Tools currently in the form; supplies URLs for already-configured images. */
  getCurrentTools?: () => FormUnifiedTool[] | undefined
  onApply: (tools: FormUnifiedTool[]) => void
}): ApplySuggestionHandler {
  return async (raw: string) => {
    const trimmed = (raw || "").trim()
    let parsed: unknown
    try {
      parsed = trimmed ? JSON.parse(trimmed) : []
    } catch {
      return false
    }
    if (!Array.isArray(parsed)) return false

    const validAgentIds = new Set(opts.agents.map((a) => String(a.id)))
    const knownImageUrls = new Map<string, string>()
    for (const current of opts.getCurrentTools?.() ?? []) {
      for (const field of current.customFormSchema?.fields ?? []) {
        const image = field.descriptionImage
        if (image?.attachmentId && image.url) {
          knownImageUrls.set(image.attachmentId, image.url)
        }
      }
    }
    const incoming = parsed as Array<Record<string, unknown>>
    const tools: FormUnifiedTool[] = []
    for (const item of incoming) {
      if (!item || typeof item !== "object") continue
      const { tool, hardFail } = normalizeSchemaItem(item, {
        validAgentIds,
        tabularToolsEnabled: opts.tabularToolsEnabled === true,
        knownImageUrls,
      })
      if (hardFail) return false
      if (tool) tools.push(tool)
    }

    if (tools.length === 0) return false
    opts.onApply(tools)
    return true
  }
}
